import { Button, Checkbox, Header, Icon, Modal, Table, Tooltip } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { ModalConfirm } from '@/components/Autre';
import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import type { ModeReglementType, RegieModeReglementType } from '@/types';

type Direction = 'up' | 'down';

type ModalRegieModesReglementProps = {
  onClose: () => void;
  onValidate: (modes: RegieModeReglementType[]) => void;
  selectedModes?: RegieModeReglementType[];
  depotFondTresor?: boolean;
};

const ModalRegieModesReglement = (props: ModalRegieModesReglementProps) => {
  const { onClose, onValidate, selectedModes, depotFondTresor = false, ...rest } = props;

  const [_selectedModes, setSelectedModes] = React.useState<RegieModeReglementType[]>([]);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { modesReglements, modesReglementsActifs, regieModesReglement } =
    React.useContext(ModeReglementContext);

  /** Liste des modes non sélectionnés dans cette régie */
  const unselectedModes = React.useMemo(
    () => _.differenceBy(modesReglementsActifs, _selectedModes, 'identifiant'),
    [modesReglementsActifs, _selectedModes],
  );

  /** Liste des modes inactifs restant */
  const disabledModes = React.useMemo(
    () => _.filter(modesReglements, { actif: false }),
    [modesReglements],
  );

  /**
   * Fonction permettant d'ajouter ou supprimer un mode de règlement à la régie
   */
  const handleModeToggle = React.useCallback(
    (mode: ModeReglementType | RegieModeReglementType, force?: boolean) => () => {
      // S'il l'élément est selectionné
      if (_.some(_selectedModes, { identifiant: mode.identifiant })) {
        // On retire le mode de règlement du state puis on réindex les ordres pour combler les trous
        setSelectedModes((prev) =>
          _.map(_.reject(prev, { identifiant: mode.identifiant }), (m, index) => ({
            ...m,
            ordre: index,
          })),
        );
      } else if (mode.code === MODES_REGLEMENTS.PRELEVEMENT && !depotFondTresor && !force) {
        // DFT obligatoire si on essaye d'ajouter le prélèvement
        setConfirmOpen(true);
      } else {
        // Sinon on veut le rajouter
        setSelectedModes((prev) => [
          ...prev,
          {
            identifiant: mode.identifiant,
            libelle: mode.libelle,
            code: mode.code,
            ordre: _.size(_selectedModes),
          },
        ]);
      }
    },
    [_selectedModes, depotFondTresor],
  );

  /**
   * Fonction permettant de déplacer vers le haut ou vers le bas un mode
   * Doit réindexer tous les "ordres" du tableau en fonction de la direction
   */
  const handleModeMove = React.useCallback(
    (movedMode: RegieModeReglementType, direction: Direction) => {
      const reindexed = _.map(_selectedModes, (mode) => {
        if (mode.identifiant === movedMode.identifiant) {
          return {
            ...mode,
            ordre: mode.ordre + (direction === 'up' ? -1 : 1),
          };
        }

        // sinon si on est sur un autre, on doit incrémenter ou décrémenter l'ordre
        // s'il est juste avant ou juste après le mode déplacé
        if (mode.ordre === movedMode.ordre + (direction === 'up' ? -1 : 1)) {
          return {
            ...mode,
            ordre: mode.ordre + (direction === 'up' ? 1 : -1),
          };
        }

        return mode;
      });

      const reordered = _.orderBy(reindexed, 'ordre');

      setSelectedModes(reordered);
    },
    [_selectedModes],
  );

  /** Sélectionne ou désélectionne tous les modes de règlements.
   * Exclu les inactifs */
  const handleSelectAll = React.useCallback(
    (e, { checked }) => {
      if (checked) {
        setSelectedModes((prev) => [
          ...prev,
          ..._.map(unselectedModes, (mode, i) => ({
            identifiant: mode.identifiant,
            libelle: mode.libelle,
            code: mode.code,
            ordre: _.size(_selectedModes) + i,
          })),
        ]);
      } else {
        setSelectedModes([]);
      }
    },
    [_selectedModes, unselectedModes],
  );

  const handlePrelevementConfirm = React.useCallback(() => {
    setConfirmOpen(false);
    handleModeToggle(_.find(modesReglementsActifs, { code: MODES_REGLEMENTS.PRELEVEMENT }), true)();
  }, [handleModeToggle, modesReglementsActifs]);

  const handlePrelevementCancel = React.useCallback(() => {
    setConfirmOpen(false);
  }, []);

  // On met les modes de règlements sélectionnés dans un state pour pouvoir les manipuler
  React.useEffect(() => {
    if (!_.isNil(selectedModes)) {
      setSelectedModes(selectedModes);
    } else if (!_.isNil(regieModesReglement)) {
      setSelectedModes(selectedModes);
    }
  }, [selectedModes, regieModesReglement, setSelectedModes]);

  // On doit afficher ceux présent dans la régie en premier,
  // les ordonner par ordre paramétré dans la régie
  // Puis le reste des modes de règlements
  const orderedAll = React.useMemo<Array<ModeReglementType | RegieModeReglementType>>(() => {
    // On le refait au cas où
    const orderedRegieModes = _.orderBy(_selectedModes, 'ordre');

    return [...orderedRegieModes, ...unselectedModes, ...disabledModes];
  }, [disabledModes, _selectedModes, unselectedModes]);

  return (
    <Modal closeIcon open size="tiny" onClose={onClose} {...rest}>
      <Modal.Header>Sélection des modes de règlement autorisés par la régie</Modal.Header>
      <Modal.Content>
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: 40 }} textAlign="center">
                <Checkbox
                  data-testid="checkbox-select-all-modes"
                  checked={_.size(_selectedModes) === _.size(modesReglementsActifs)}
                  onChange={handleSelectAll}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Modes de règlement
                  <Tooltip
                    type="warning"
                    trigger={
                      <Icon name="Info" iconSet="Lucide" size={22} style={{ marginLeft: 10 }} />
                    }
                    message={
                      <div style={{ width: 400 }}>
                        Les modes de règlement autorisés par la régie apparaissent dans cet ordre
                        dans la fenêtre d&apos;encaissement des factures.
                        <br />
                        <br />
                        Vous pouvez librement changer l&apos;ordre des modes de règlement en
                        cliquant sur les flèches correspondantes.
                      </div>
                    }
                    position="bottom center"
                  />
                </div>
              </Table.HeaderCell>

              <Table.HeaderCell
                style={{
                  width: 40,
                  margin: 0,
                  padding: 0,
                  borderLeft: 0,
                }}
                textAlign="center"
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    fontSize: '0.9em',
                  }}
                >
                  <Icon
                    name="ChevronUp"
                    iconSet="Lucide"
                    size={20}
                    strokeWidth={3.5}
                    className="colored"
                    style={{ marginBottom: '-3px' }}
                  />
                  ordre
                  <Icon
                    name="ChevronDown"
                    iconSet="Lucide"
                    size={20}
                    strokeWidth={3.5}
                    className="colored"
                    style={{ marginTop: '-3px' }}
                  />
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {_.map(orderedAll, (mode, index) => (
              <Table.Row
                key={`mode-${mode.identifiant}`}
                data-testid={`row-mode-${mode.identifiant}`}
                disabled={'actif' in mode ? !mode.actif : false}
                style={{ height: 45 }}
              >
                <Table.Cell
                  textAlign="center"
                  selectable
                  onClick={handleModeToggle(mode)}
                  style={{ cursor: 'pointer' }}
                >
                  <Checkbox
                    data-testid={`checkbox-select-mode-${mode.identifiant}`}
                    checked={_.some(_selectedModes, { identifiant: mode.identifiant })}
                  />
                </Table.Cell>
                <Table.Cell>{mode.libelle}</Table.Cell>
                <Table.Cell
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderLeft: 0,
                  }}
                >
                  {'ordre' in mode && (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {/* On affiche pas pour le premier */}
                      {index > 0 ? (
                        <Icon
                          data-testid={`bouton-mode-up-${mode.identifiant}`}
                          name="ChevronUp"
                          iconSet="Lucide"
                          size={16}
                          strokeWidth={3.5}
                          onClick={() => handleModeMove(mode, 'up')}
                          className="colored link"
                          style={{
                            marginBottom: '-1.7px',
                            transform: 'scale(1.25)',
                            transformOrigin: 'center',
                          }}
                        />
                      ) : (
                        <div style={{ height: 13 }} />
                      )}
                      <span
                        style={{
                          fontSize: '0.75em',
                          fontWeight: 'bold',
                          pointerEvents: 'none',
                        }}
                      >
                        {'ordre' in mode && mode.ordre + 1}
                      </span>
                      {/* On affiche pas pour le dernier */}
                      {index + 1 < _.size(_selectedModes) ? (
                        <Icon
                          data-testid={`bouton-mode-down-${mode.identifiant}`}
                          name="ChevronDown"
                          iconSet="Lucide"
                          size={16}
                          strokeWidth={3.5}
                          link
                          onClick={() => handleModeMove(mode, 'down')}
                          className="colored link"
                          style={{
                            marginTop: '-1.7px',
                            transform: 'scale(1.25)',
                            transformOrigin: 'center',
                          }}
                        />
                      ) : (
                        <div style={{ height: 13 }} />
                      )}
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        {confirmOpen && (
          <ModalConfirm
            size="tiny"
            header="Dépôts de Fonds au Trésor obligatoire"
            content={
              <Header as="h4">
                <Header.Content>
                  <Header.Subheader>
                    Pour pouvoir sélectionner le mode de règlement <b>Prélèvement</b>, le Dépôts de
                    Fonds au Trésor doit être paramétré.
                    <br />
                    <br />
                    Vous devrez renseigner l&apos;intitulé du compte ainsi que son IBAN.
                  </Header.Subheader>
                  <br />
                  Souhaitez-vous poursuivre et activer le Dépôts de Fonds au Trésor ?
                </Header.Content>
              </Header>
            }
            onConfirm={handlePrelevementConfirm}
            onCancel={handlePrelevementCancel}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-close"
          onClick={onClose}
          basic
          size="tiny"
          icon="close"
          content="Annuler"
        />
        <Button
          data-testid="button-validate"
          positive
          onClick={() => onValidate(_selectedModes)}
          disabled={_.isEmpty(_selectedModes)}
          content="Valider"
          icon="checkmark"
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRegieModesReglement;
